import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from './store/AuthContext.js';
import { Button } from './ui/Button.js';
import { t } from './i18n/index.js';
import './App.css';

/**
 * Coquille applicative : en-tete, navigation, zone de contenu et pied de page.
 * Le lien d'evitement est le premier element focusable, conformement aux regles
 * d'accessibilite clavier.
 */
export function App() {
  const labels = t();
  const auth = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout(): Promise<void> {
    setLoggingOut(true);
    try {
      await auth.logout();
      void navigate('/', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <div className="fg-app">
      <a className="skip-link" href="#contenu">
        {labels.app.skipToContent}
      </a>

      <header className="fg-app__header">
        <Link to="/" className="fg-app__brand" aria-label={labels.app.name}>
          <span className="fg-app__brand-mark" aria-hidden="true" />
          <span className="fg-app__brand-text">{labels.app.name}</span>
        </Link>

        <nav className="fg-app__nav" aria-label={labels.nav.home}>
          <NavLink to="/" end className="fg-app__nav-link">
            {labels.nav.home}
          </NavLink>
          {auth.isAuthenticated && (
            <NavLink to="/profil" className="fg-app__nav-link">
              {labels.nav.profile}
            </NavLink>
          )}
        </nav>

        <div className="fg-app__account">
          {auth.initializing ? (
            <span className="fg-app__account-name">{labels.app.loading}</span>
          ) : auth.isAuthenticated ? (
            <>
              <span className="fg-app__account-name">{auth.user?.displayName}</span>
              <Button variant="ghost" size="sm" busy={loggingOut} onClick={handleLogout}>
                {labels.nav.logout}
              </Button>
            </>
          ) : (
            <>
              <Link className="fg-app__nav-link" to="/connexion">
                {labels.nav.login}
              </Link>
              <Link className="fg-app__nav-link fg-app__nav-link--cta" to="/inscription">
                {labels.nav.register}
              </Link>
            </>
          )}
        </div>
      </header>

      <main id="contenu" className="fg-app__main" tabIndex={-1}>
        <Outlet />
      </main>

      <footer className="fg-app__footer">
        <span>
          {labels.app.name} - {labels.app.tagline}
        </span>
      </footer>
    </div>
  );
}
